import { useMemo, useState } from 'react';
import '@/components/lists/lists.css';
import type { Answer, Claim, Deadline } from '../types';
import { deadlineLead, useAppealWindows, withAppealWindow } from '../appealWindows';
import { billedOf, collapseCodes, daysLeft, formatDateFull, money, ROW_REASON } from '../utils';
import { shortOf } from '../store';
import { CardRow } from './CardRow';
import { Link } from 'react-router-dom';

type Group = 'needs_you' | 'appeal' | 'resend' | 'bill' | 'reprocess';

const TAG: Record<Answer, { label: string; cls: string }> = {
  appeal: { label: 'Appeal', cls: 'b-info' },
  resend: { label: 'Resend', cls: 'b-info' },
  reprocess: { label: 'Reprocess', cls: 'b-info' },
  bill_patient: { label: 'Bill patient', cls: 'b-mute' },
  needs_you: { label: 'Needs you', cls: 'b-warn' },
};

const DENIED_ORDER: Group[] = ['needs_you', 'appeal', 'resend', 'bill'];
const UNDERPAID_ORDER: Group[] = ['reprocess', 'appeal', 'needs_you'];
const GROUP_LABEL: Record<Group, string> = {
  needs_you: 'Needs you',
  appeal: 'Appeal ready',
  resend: 'Resend',
  bill: 'Bill the patient',
  reprocess: 'Ask the payer to reprocess',
};

function groupOf(c: Claim): Group {
  switch (c.answer) {
    case 'appeal':
      return 'appeal';
    case 'resend':
      return 'resend';
    case 'reprocess':
      return 'reprocess';
    case 'bill_patient':
      return 'bill';
    default:
      return 'needs_you';
  }
}

/** The deadline date with the practice's appeal window for this payer applied (Settings › Appeals). */
function deadlineOf(c: Claim): Deadline | null {
  if (c.answer === 'appeal') return c.deadline ? withAppealWindow(c.deadline, c.payer) : null;
  if (c.answer === 'reprocess') return c.reprocess ? withAppealWindow(c.reprocess.deadline, c.payer, 'payment') : null;
  return null;
}
function deadlineDate(c: Claim): string | null {
  return deadlineOf(c)?.date ?? null;
}

function Row({
  c,
  view,
  selected,
  onSelect,
}: {
  c: Claim;
  view: 'denied' | 'underpaid';
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const amount = view === 'underpaid' ? `${money(shortOf(c))} short` : money(billedOf(c.procedures));
  const tag = TAG[c.answer];
  const reason = ROW_REASON[c.id] ?? '';
  const dl = deadlineOf(c);
  const dd = dl?.date ?? null;
  const a11y = `${c.patient}, ${amount}, ${c.payer.short}, ${collapseCodes(c.procedures)}, ${tag.label}: ${reason}`;
  return (
    <CardRow
      href={`/denied/${c.id}`}
      selected={selected}
      onSelect={() => { onSelect(c.id); }}
      name={c.patient}
      amount={amount}
      sub={`${c.payer.short} · ${collapseCodes(c.procedures)}`}
      tag={tag}
      taglineText={reason}
      {...(dl && dd ? { metaLine: `${deadlineLead(dl)} ${formatDateFull(dd)} · ${String(daysLeft(dd))} days` } : {})}
      a11yLabel={a11y}
    />
  );
}

export function Queue({
  view,
  claims,
  selectedId,
  onSelect,
  onSearchAllTime,
  postingCount,
  resolvedCount,
  onResolved,
}: {
  view: 'denied' | 'underpaid';
  claims: Claim[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onSearchAllTime?: (query: string) => void;
  /** Resolved history for this tab: a link at the foot of the list (rcm-18 §5.5). */
  resolvedCount?: number;
  onResolved?: () => void;
  postingCount: number;
}) {
  const windows = useAppealWindows();
  const [query, setQuery] = useState('');
  const [payer, setPayer] = useState('');
  const order = view === 'denied' ? DENIED_ORDER : UNDERPAID_ORDER;

  // Payers present in this queue, for the filter select (00-ds-09 §9: a payer
  // filter once the list passes ~30; always rendering it is fine).
  const payers = useMemo(() => {
    const seen = new Set<string>();
    for (const c of claims) seen.add(c.payer.short);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [claims]);

  const byGroup = useMemo(() => {
    void windows; // deadlines (and so the order inside a group) follow the practice's appeal windows
    const q = query.trim().toLowerCase();
    const filtered = claims.filter((c) => {
      if (payer && c.payer.short !== payer) return false;
      if (!q) return true;
      return (
        c.patient.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        c.memberId.toLowerCase().includes(q) ||
        c.payer.name.toLowerCase().includes(q) ||
        c.payer.short.toLowerCase().includes(q) ||
        c.procedures.some((p) => p.code.toLowerCase().includes(q))
      );
    });
    const map = new Map<Group, Claim[]>();
    for (const g of order) map.set(g, []);
    for (const c of filtered) map.get(groupOf(c))?.push(c);
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const da = deadlineDate(a);
        const db = deadlineDate(b);
        if (da && db && da !== db) return da < db ? -1 : 1;
        const amtA = view === 'underpaid' ? shortOf(a) : billedOf(a.procedures);
        const amtB = view === 'underpaid' ? shortOf(b) : billedOf(b.procedures);
        return amtB - amtA;
      });
    }
    return map;
  }, [claims, query, payer, order, view, windows]);

  return (
    <aside className="apl-queue" aria-label={view === 'denied' ? 'Denied claims' : 'Underpaid claims'}>
      <div className="apl-search-wrap">
        <label className="apl-visually-hidden" htmlFor="apl-search">
          Search {view} claims
        </label>
        <input
          id="apl-search"
          className="apl-search"
          placeholder={`Search ${view} claims`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
        />
        {payers.length > 1 ? (
          <select
            className="lx-select"
            style={{ marginTop: 8, width: '100%' }}
            value={payer}
            aria-label="Filter by payer"
            onChange={(e) => { setPayer(e.target.value); }}
          >
            <option value="">All payers</option>
            {payers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        ) : null}
      </div>
      {order.every((g) => (byGroup.get(g)?.length ?? 0) === 0) ? (
        <div className="apl-empty" style={{ padding: '24px 4px' }}>
          <p className="apl-empty-title">
            {query.trim()
              ? `No ${view} claims match “${query.trim()}”.`
              : payer
                ? `No ${view} claims for ${payer}.`
                : `No ${view} claims.`}
          </p>
          {query.trim() && onSearchAllTime ? (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="apl-btn apl-btn-sm"
                onClick={() => { onSearchAllTime(query.trim()); }}
              >
                Search all time
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        order.map((g) => {
          const rows = byGroup.get(g) ?? [];
          if (rows.length === 0) return null;
          return (
            <section key={g}>
              <h2 className="apl-group-head">
                {GROUP_LABEL[g]} · {rows.length}
              </h2>
              {rows.map((c) => (
                <Row key={c.id} c={c} view={view} selected={c.id === selectedId} onSelect={onSelect} />
              ))}
            </section>
          );
        })
      )}
      {onResolved ? (
        <button type="button" className="apl-resolved-link" onClick={onResolved}>
          Resolved · {String(resolvedCount ?? 0)} claims →
        </button>
      ) : null}
      {view === 'underpaid' && postingCount > 0 ? (
        <div className="apl-postingnote">
          {postingCount} other partial payments match what the patients’ plans cover, so they aren’t underpaid. The patients
          owe the difference.{' '}
          <Link to="/posting" className="apl-src" style={{ textDecoration: 'underline' }}>
            See them in Posting
          </Link>
        </div>
      ) : null}
    </aside>
  );
}
